/*================================================================
‘Copyright © 2020, Cheers Interactive Pvt Ltd.  All rights reserved.
      File Description :  Company overview tab validations
 --------------------------------------------------------------------------------- 
    Creation Details 
    Date Created				: 25/Jul/2020 
    Author						: Aditya Tijare
================================================================ 
*/
import * as Yup from "yup";
import * as REGEX from "../../../../config/regexValidation";
import { COLLABORATION_TYPES } from "../constants";
import { removeHtmlTags } from "utils";
export const companyOverviewValidationSchema = {
  companyName: Yup.string()
    .required("Please enter company name")
    .max(125, "Company name can not be more than 125 characters")
    .matches(REGEX.COMPANY, "Please enter valid company name"),
  companyFoundedYear: Yup.string().matches(REGEX.YEAR, "Please enter valid Founded Year"), //Years from 1000 to 2999
  companySocialTwitter: Yup.string().matches(REGEX.TWITTER, "Please enter valid Twitter handel URL"),
  companySocialFacebook: Yup.string().matches(REGEX.FACEBOOK, "Please enter valid Facebook page URL"),
  companySocialLinkedIn: Yup.string().matches(REGEX.LINKEDIN, "Please enter valid LinkedIn page URL"),
  companyEmail: Yup.string().email("Please enter valid email"),
  companyWebsite: Yup.string().required("Please enter company website").matches(REGEX.URL, "Please enter valid company website URL"),
  companyPublishedDate: Yup.string().required("Please select the publish date").nullable(),
  companySubContentType: Yup.string().when("pVariant", {
    is: (obj) => {
      return obj && obj === "Insider";
    },
    then: Yup.string().required("Please select sub content type"),
    otherwise: Yup.string().nullable(),
  }),
  companyDeliveryType: Yup.string().when("pVariant", {
    is: (obj) => {
      return obj && obj === "Insider";
    },
    then: Yup.string().required("Please select delivery type"),
    otherwise: Yup.string().nullable(),
  }),
};

export const collaborationValidationSchema = {
  // a) Collaboration Name
  collaborationName: Yup.string()
    .matches(/^[\s\S]*$/, "Please enter the valid name.") // allows all characters
    .min(2, "Name must be at least 2 characters.")
    .max(200, "You have exceeded the maximum character limit of 200.")
    .required("Please enter the name."),

  // b) Collaboration Type
  collaborationType: Yup.string().required("Please select the type."),

  // c) Deal Value (only digits, min value: 1)
  collaborationDealValue: Yup.string().matches(/^\d+$/, "Deal value must be a valid integer.").required("Please enter the deal value."),

  collaborationDate: Yup.date().max(new Date(), "Deal date cannot be in the future."),

  // d) Deal Date (handled via date picker, assumed controlled elsewhere)

  // e) Source URL with strict format
  collaborationSourceUrl: Yup.string().matches(
    /^(https?:\/\/)(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/,
    "Please enter a valid source URL."
  ),

  // f) Collaboration Description
  collaborationDescription: Yup.string()
    .test("plain-empty-text", "Please enter the description.", (value) => {
      const plainText = removeHtmlTags(value)?.trim() || "";
      return plainText.length !== 0;
    })
    .test("plain-text-length", "Description must be at least 25 characters.", (value) => {
      const plainText = removeHtmlTags(value)?.trim() || "";
      return plainText.length >= 25;
    })
    .test("plain-text-max-length", "You have exceeded the maximum character limit of 1000.", (value) => {
      const plainText = removeHtmlTags(value) || "";
      return plainText.length <= 1000;
    })
    .required("Please enter the description"),

  // g) Technology Purpose
  collaborationTechnologyPurpose: Yup.string()
    .test("optional-plain-text", "Technology purpose must be at least 25 characters", (value) => {
      const plainText = removeHtmlTags(value)?.trim() || "";
      if (!plainText) return true; // skip validation if empty
      return plainText.length >= 25;
    })
    .test("optional-plain-text-max", "Technology purpose cannot be more than 1000 characters", (value) => {
      const plainText = removeHtmlTags(value) || "";
      if (!plainText) return true; // skip validation if empty
      return plainText.length <= 1000;
    }),
  // Funding Type: required only if Collaboration Type is FUNDING_INVESTMENT
  fundingTypeRound: Yup.string().when("collaborationType", {
    is: COLLABORATION_TYPES.FUNDING_INVESTMENT,
    then: Yup.string().required("Please select funding type"),
    otherwise: Yup.string().nullable(),
  }),
};
